import React, { useState, useEffect } from 'react'
import useTranslation from 'next-translate/useTranslation'
import { StaticImageData } from 'next/image'
import Link from 'next/link'
import {
  InfoWrapper,
  DesktopHeroWrapper,
  PricePerUnit,
  Spacer,
  Description,
  HeroHeader,
  ItemCounterWrapper,
  Discount,
  PriceWrapper,
  BadgeWrapper,
  BadgeOutline,
} from './Hero.styles'
import {
  ContentContainer,
  MobileWrapper,
  DesktopWrapper,
} from 'features/components/ProductDetailHero/ProductDetailHero.styles'
import { Text } from 'components/Text'
import { Stack } from 'components/Stack'
import { HeroImage } from 'features/components/ProductDetailHero/components/HeroImage'
import { InformationCollapse, InformationCollapseType } from 'components/InformationCollapse'
import { ProductDetailHero } from 'features/components/ProductDetailHero'
import { ItemCounter } from 'features/components/ProductDetailHero/components/ItemCounter/ItemCounter'
import Trans from 'next-translate/Trans'
import { HeroTitle } from 'features/components/ProductDetailHero/components/HeroHeader/HeroHeader.styles'
import { ProductVariant } from 'models/Product'
import { Button } from 'components/Button'
import { AddToCart } from "features/ECommerce/hooks/useAddToCart"

type HeroProps = {
  thumbnail: StaticImageData
  box: 'big-box' | 'classic-box' | 'small-box'
  product: ProductVariant | undefined
  onAdd: AddToCart
}

export const Hero = ({ thumbnail, onAdd, box, product }: HeroProps) => {
  const { t } = useTranslation()
  const [quantity, setQuantity] = useState(1)

  if (!product) return null

  const pricePerUnit = product.price.subtotal / 100
  const discountedPrice = product.price.total / 100
  const percentOff = Math.round(((pricePerUnit - discountedPrice) * 100) / pricePerUnit)

  const infoList: InformationCollapseType[] = [
    {
      label: 'products:safetyQuality.safety.title',
      description: 'products:safetyQuality.safety.description',
    },
    {
      label: 'products:essentials.hero.feedingGuidelines.title',
      description: 'products:essentials.hero.feedingGuidelines.description',
    },
    {
      label: 'products:essentials.hero.firstTime.title',
      description: 'products:essentials.hero.firstTime.description',
    },
  ]

  const handleAdd = () => setQuantity((prev) => prev + 1)

  const handleRemove = () => {
    if (quantity > 1) {
      setQuantity((prev) => prev - 1)
    }
  }

  return (
    <ProductDetailHero>
      <DesktopHeroWrapper>
        <HeroImage thumbnail={thumbnail} alt={t('products:essentials.hero.thumbnailAlt')} />
        <BadgeOutline>
          <BadgeWrapper>
            <Text align="center" size="h5" color="accent-white" weight="semiBold">
              <Trans components={{ br: <br /> }} i18nKey={'products:essentials.hero.badge.title'} />
            </Text>
            <Text align="center" size="headline" color="accent-white">
              {t('products:essentials.hero.badge.subtitle')}
            </Text>
          </BadgeWrapper>
        </BadgeOutline>
      </DesktopHeroWrapper>
      <ContentContainer>
        <MobileWrapper>
          <Stack
            css={{ marginBottom: '$5' }}
            width="fullWidth"
            direction="column"
            alignItems="center"
            gap="2"
          >
            <Text size="h5" color="accent-black">
              {t('products:essentials.hero.selectSize')}
            </Text>
            <Stack width="fullWidth" justifyContent="center" direction="column" gap="2">
              <Link href="small-box">
                <Button variant={box === 'small-box' ? 'primary' : 'tertiary'}>
                  {t('products:essentials.hero.small-box')}
                </Button>
              </Link>
              <Link href="classic-box">
                <Button variant={box === 'classic-box' ? 'primary' : 'tertiary'}>
                  {t('products:essentials.hero.classic-box')}
                </Button>
              </Link>
              <Link href="big-box">
                <Button variant={box === 'big-box' ? 'primary' : 'tertiary'}>
                  {t('products:essentials.hero.big-box')}
                </Button>
              </Link>
            </Stack>
          </Stack>
        </MobileWrapper>
        <Stack
          width="fullWidth"
          direction="column"
          alignItems="center"
          gap={{ '@initial': '2', '@bp3': '5' }}
        >
          <HeroHeader>
            <HeroTitle>{t(`products:essentials.hero.${box}`)}</HeroTitle>
            <Text weight="semiBold" color="accent-black" size="bodyExtraLarge">
              {t(`products:essentials.hero.subtitle.${box}`)}
            </Text>
            <Description
              align="center"
              size={{ '@initial': 'body', '@bp3': 'bodyLarge' }}
              color="accent-black"
            >
              <Trans
                i18nKey={`products:essentials.hero.${box}-description`}
                components={{ br: <br /> }}
              />
            </Description>
          </HeroHeader>
          <MobileWrapper>
            <HeroImage thumbnail={thumbnail} alt={t('products:essentials.hero.thumbnailAlt')} />
          </MobileWrapper>
          <DesktopWrapper>
            <Stack width="fullWidth" direction="column" alignItems="center" gap="2">
              <Text size="h5" color="accent-black">
                {t('products:essentials.hero.selectSize')}
              </Text>
              <Stack width="fullWidth" justifyContent="center" direction="row" gap="2">
                <Link href="small-box">
                  <Button variant={box === 'small-box' ? 'primary' : 'tertiary'}>
                    {t('products:essentials.hero.small-box')}
                  </Button>
                </Link>
                <Link href="classic-box">
                  <Button variant={box === 'classic-box' ? 'primary' : 'tertiary'}>
                    {t('products:essentials.hero.classic-box')}
                  </Button>
                </Link>
                <Link href="big-box">
                  <Button variant={box === 'big-box' ? 'primary' : 'tertiary'}>
                    {t('products:essentials.hero.big-box')}
                  </Button>
                </Link>
              </Stack>
            </Stack>
          </DesktopWrapper>
          <Stack direction="column" alignItems="center" width="fullWidth">
            <PriceWrapper>
              <PricePerUnit size="h5" color="accent-black">
                ${pricePerUnit} / box
              </PricePerUnit>
            </PriceWrapper>
            <ItemCounterWrapper>
              <ItemCounter counter={quantity} onAdd={handleAdd} onRemove={handleRemove} />
            </ItemCounterWrapper>
            <Button
              onClick={() => onAdd(product.productVariantId, product.productId, quantity, product.name)}
              variant="primary"
            >
              {t('products:fivePound.cta')}
            </Button>
            {percentOff > 0 && (
              <Discount>
                {t('products:essentials.hero.save-alert')} <strong>{`$${discountedPrice}`}</strong>{' '}
                ({percentOff}% off)
              </Discount>
            )}
          </Stack>
        </Stack>
        <Spacer />
        <InfoWrapper>
          <InformationCollapse infoList={infoList} />
        </InfoWrapper>
      </ContentContainer>
    </ProductDetailHero>
  )
}

Hero.displayName = 'Hero'
